import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withStyles } from "@material-ui/core/styles";
import {
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    TextField,
    Tooltip,
    Typography,
    WithStyles
} from "@material-ui/core";
import { styles } from "./Contracts.styles";
import * as ContractAction from "../../../../stores/contracts/ContractAction";
import { convertDateStringToNumber, isDateSameOrAfterDate, mapNumberToDate } from "../../../../utilities/dateTimeUtils";
import CloseIcon from "@material-ui/icons/Close";
import { DATE_FORMAT } from "../../../../constants/TIME";
import { BlueButton } from "../../../components/Buttons/BlueButton";
import { FormDateField } from "../../../components/FormComponents/FormDateField";
import { FormTextField } from "../../../components/FormComponents/FormTextField";
import { Autocomplete } from "@material-ui/lab";
import * as BusinessPartnerAction from "../../../../stores/businessPartners/BusinessPartnerAction";
import BusinessPartnerModel from "../../../../models/businessPartner/BusinessPartnerModel";
import ContractTypeModel from "../../../../models/contract/ContractTypeModel";
import InformationProductModel from "../../../../models/contract/InformationProductModel";
import { selectRequesting } from "../../../../selectors/requesting/RequestingSelector";

export type ContractDialogType = "createContract" | "editContract";

export interface IContractData {
    bpId: number;
    contractTypeId: number;
    contractProductId: number;
    clmMaNumber?: string;
    startDate: number;
    endDate: number;
    affiliates?: number;
    remark?: string;
    timeSeriesFrom?: number;
    timeSeriesTo?: number;
}

export interface ICreateContractDialogProps extends WithStyles<typeof styles> {
    dialogType: ContractDialogType;
    onConfirm: (type: ContractDialogType, reportData: IContractData) => void;
    onClose: () => void;
    contract: IContractData;
}

const CreateContractDialogPure: React.FunctionComponent<ICreateContractDialogProps> = (
    props: ICreateContractDialogProps
) => {
    const { dialogType, contract, onConfirm, onClose, classes } = props;
    const {
        bpId,
        contractTypeId,
        contractProductId,
        clmMaNumber,
        timeSeriesFrom,
        timeSeriesTo,
        remark,
        startDate,
        endDate,
        affiliates
    } = contract || {};

    const dispatch = useDispatch();

    const [actualBPId, setActualBPId] = useState<number>(bpId || null);
    const [actualContractTypeId, setActualContractTypeId] = useState<number>(contractTypeId);
    const [actualProductId, setActualProductId] = useState<number>(contractProductId);
    const [actualClmMaNumber, setActualClmMaNumber] = useState<string>(clmMaNumber);
    const [actualTimeSeriesFrom, setActualTimeSeriesFrom] = useState<number>(timeSeriesFrom);
    const [actualTimeSeriesTo, setActualTimeSeriesTo] = useState<number>(timeSeriesTo);
    const [actualStartDate, setActualStartDate] = useState<number>(startDate);
    const [actualEndDate, setActualEndDate] = useState<number>(endDate);
    const [actualRemark, setActualRemark] = useState<string>(remark);
    const [actualAffiliates, setActualAffiliates] = useState<number>(affiliates);
    const [isTimeSeriesUsed, setIsTimeSeriesUsed] = useState<boolean>(!!timeSeriesFrom || !!timeSeriesTo);

    const businessPartnersFromState = useSelector(state => state.businessPartners);
    const contractTypeFromState = useSelector(state => state.contracts.types);
    const productsFromState = useSelector(state => state.contracts.products); // information products

    let businessPartners;
    if (businessPartnersFromState) {
        businessPartners = Object.values(businessPartnersFromState)?.map((businessPartner: BusinessPartnerModel) => ({
            bpId: businessPartner.bpId,
            bpName: businessPartner.bpName,
            countryName: businessPartner.countryName
        }));
    }

    let contractTypes;
    if (contractTypeFromState) {
        contractTypes = Object.values(contractTypeFromState)?.map((contractType: ContractTypeModel) => ({
            id: contractType.id,
            name: contractType.name,
            validFrom: contractType.validFrom,
            validTo: contractType.validTo
        }));
    }

    let products;
    if (productsFromState) {
        products = Object.values(productsFromState)?.map((product: InformationProductModel) => ({
            id: product.id,
            name: product.name,
            affiliates: product.affiliates,
            timeSeries: product.timeSeries,
            remark: product.remark
        }));
    }

    const actualProduct = products?.find(p => p.id === actualProductId);

    const isRequesting = useSelector(state =>
        selectRequesting(state, [
            ContractAction.REQUEST_ALL_CONTRACT_TYPES,
            ContractAction.REQUEST_ALL_INFORMATION_PRODUCTS,
            BusinessPartnerAction.REQUEST_ALL_BUSINESS_PARTNERS
        ])
    );

    useEffect(() => {
        if (!actualProduct?.timeSeries) {
            setActualTimeSeriesFrom(undefined);
            setActualTimeSeriesTo(undefined);
        }
        if (!actualProduct?.remark) {
            setActualRemark(undefined);
        }
        if (!actualProduct?.affiliates) {
            setActualAffiliates(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actualProductId]);

    useEffect(() => {
        dispatch(ContractAction.requestAllContractTypes());
        dispatch(ContractAction.requestAllInformationProducts());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClose = () => {
        onClose();
    };

    interface IDialogTypeConfig {
        title: string;
        confirmButtonTitle: string;
    }

    interface errorReason {
        condition: () => boolean;
        error: string;
    }

    const getError = () => {
        const errorReasons: errorReason[] = [
            {
                condition: () => !actualBPId,
                error: "Please select a Business partner"
            },
            {
                condition: () => !actualContractTypeId,
                error: "Please select a Contract type"
            },
            {
                // GUI field was renamed from "CLM MA-number" to "SAP CRM Contract ID"
                condition: () => !actualClmMaNumber || actualClmMaNumber?.length === 0,
                error: "Please fill a SAP CRM Contract ID"
            },
            {
                condition: () => !actualProductId,
                error: "Please select an Information product "
            },
            {
                condition: () => !isDateSameOrAfterDate(actualEndDate, actualStartDate),
                error: "Start date must not be after End date."
            },
            {
                condition: () => isTimeSeriesUsed && !isDateSameOrAfterDate(actualTimeSeriesTo, actualTimeSeriesFrom),
                error: "From date must not be after To date."
            },
            // fallback
            {
                condition: () => true,
                error: ""
            }
        ];
        return errorReasons.find(eR => eR.condition())?.error;
    };

    const dialogTitleMap: {
        [key in ContractDialogType]: IDialogTypeConfig;
    } = {
        createContract: {
            title: "Create a new contract",
            confirmButtonTitle: "Create"
        },
        editContract: {
            title: "Edit the contract",
            confirmButtonTitle: "Save"
        }
    };

    const handleConfirmClick = () => {
        onConfirm(dialogType, {
            bpId: actualBPId,
            contractTypeId: actualContractTypeId,
            contractProductId: actualProductId,
            clmMaNumber: actualClmMaNumber,
            ...(actualStartDate !== undefined &&
                actualStartDate !== 0 && {
                    startDate: actualStartDate
                }),
            ...(actualEndDate !== undefined &&
                actualEndDate !== 0 && {
                    endDate: actualEndDate
                }),
            ...(actualAffiliates !== undefined && {
                affiliates: actualAffiliates
            }),
            ...(actualRemark !== undefined && {
                remark: actualRemark
            }),
            ...(isTimeSeriesUsed &&
                actualTimeSeriesFrom !== undefined &&
                actualTimeSeriesFrom !== 0 && {
                    timeSeriesFrom: actualTimeSeriesFrom
                }),
            ...(isTimeSeriesUsed &&
                actualTimeSeriesTo !== undefined &&
                actualTimeSeriesTo !== 0 && {
                    timeSeriesTo: actualTimeSeriesTo
                })
        });
    };

    const handleBPChange = (event, value) => {
        setActualBPId(value?.bpId);
    };

    const error = getError();
    const isSendEnabled = error === "";

    return (
        <>
            <Dialog open fullWidth scroll="paper" onClose={handleClose} maxWidth="md">
                <DialogTitle>
                    <Typography variant="subtitle1">{dialogTitleMap[dialogType]?.title}</Typography>
                    <br />
                    <IconButton
                        aria-label="close"
                        style={{ position: "absolute", right: "1px", top: "1px" }}
                        onClick={handleClose}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {isRequesting ? (
                        <div className={classes.center}>
                            <CircularProgress color="inherit" className={classes.center} />
                        </div>
                    ) : (
                        <FormControl>
                            <div className={classes.sectionInput}>
                                <Grid container spacing={2} alignItems="stretch">
                                    <Grid item xs={12}>
                                        <Typography variant={"caption"} gutterBottom>
                                            Business partner *
                                        </Typography>
                                        <Grid item xs={12}>
                                            <Autocomplete
                                                id="selectBusinessPartner"
                                                options={businessPartners || []}
                                                getOptionLabel={(option: any) => {
                                                    return `${option.bpId} - ${option.bpName}`;
                                                }}
                                                value={businessPartners?.find(bP => bP.bpId === actualBPId)}
                                                renderInput={params => (
                                                    <TextField
                                                        {...params}
                                                        variant="outlined"
                                                        placeholder="Select Business partner"
                                                    />
                                                )}
                                                onChange={handleBPChange}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant={"caption"} gutterBottom>
                                            Contract type *
                                        </Typography>
                                        <Grid item xs={12}>
                                            <Autocomplete
                                                id="selectContractType"
                                                options={contractTypes || []}
                                                getOptionLabel={(option: any) => option.name}
                                                value={contractTypes?.find(type => type.id === actualContractTypeId)}
                                                getOptionSelected={(option, value) => {
                                                    return option.id === value.id;
                                                }}
                                                renderInput={params => (
                                                    <TextField
                                                        {...params}
                                                        variant="outlined"
                                                        placeholder="Select Contract type"
                                                    />
                                                )}
                                                onChange={(event, value) => setActualContractTypeId(value?.id)}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={2}>
                                        <Typography variant={"caption"} gutterBottom>
                                            SAP CRM Contract ID
                                        </Typography>
                                        <FormTextField
                                            label=""
                                            onChange={e => setActualClmMaNumber(e.target.value)}
                                            value={actualClmMaNumber}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant={"caption"} gutterBottom>
                                            Information product *
                                        </Typography>
                                        <Grid item xs={12}>
                                            <Autocomplete
                                                id="selectInformationProduct"
                                                options={products || []}
                                                getOptionLabel={(option: any) => option.name}
                                                value={products?.find(p => p.id === actualProductId)}
                                                getOptionSelected={(option, value) => {
                                                    return option.id === value.id;
                                                }}
                                                renderInput={params => (
                                                    <TextField
                                                        {...params}
                                                        variant="outlined"
                                                        placeholder="Select Information product"
                                                    />
                                                )}
                                                onChange={(event, value) => {
                                                    setActualProductId(value?.id);
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                    {actualProduct?.affiliates && (
                                        <Grid item xs={2}>
                                            <Typography variant={"caption"} gutterBottom>
                                                Affiliates
                                            </Typography>
                                            <FormTextField
                                                label=""
                                                onChange={e => setActualAffiliates(parseInt(e.target.value))}
                                                value={actualAffiliates}
                                                type="number"
                                            />
                                        </Grid>
                                    )}
                                    {actualProduct?.remark && (
                                        <Grid item xs={10}>
                                            <Typography variant={"caption"} gutterBottom>
                                                Remark
                                            </Typography>
                                            <FormTextField
                                                label=""
                                                onChange={e => setActualRemark(e.target.value)}
                                                value={actualRemark}
                                            />
                                        </Grid>
                                    )}
                                    {actualProduct?.timeSeries && (
                                        <Grid container item xs={12} className={classes.content}>
                                            <Grid item xs={12}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            style={{ color: "black" }}
                                                            checked={isTimeSeriesUsed}
                                                            onChange={() => setIsTimeSeriesUsed(!isTimeSeriesUsed)}
                                                        />
                                                    }
                                                    label={
                                                        <Typography variant="h4" className={classes.blackText}>
                                                            Time series
                                                        </Typography>
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Typography variant={"caption"}>Time series from</Typography>
                                                <FormDateField
                                                    placeholder="From"
                                                    value={mapNumberToDate(actualTimeSeriesFrom).format(DATE_FORMAT)}
                                                    onChange={newDate => {
                                                        setActualTimeSeriesFrom(convertDateStringToNumber(newDate));
                                                    }}
                                                    disabled={!isTimeSeriesUsed}
                                                />
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Typography variant={"caption"}>Time series to</Typography>
                                                <FormDateField
                                                    placeholder="To"
                                                    value={mapNumberToDate(actualTimeSeriesTo).format(DATE_FORMAT)}
                                                    onChange={newDate => {
                                                        setActualTimeSeriesTo(convertDateStringToNumber(newDate));
                                                    }}
                                                    disabled={!isTimeSeriesUsed}
                                                />
                                            </Grid>
                                        </Grid>
                                    )}
                                    <Grid container item xs={12}>
                                        <Grid item xs={3}>
                                            <Typography variant={"caption"}>Start date</Typography>
                                            <FormDateField
                                                placeholder="From"
                                                value={mapNumberToDate(actualStartDate).format(DATE_FORMAT)}
                                                onChange={newDate => {
                                                    setActualStartDate(convertDateStringToNumber(newDate));
                                                }}
                                                disabled={false}
                                            />
                                        </Grid>
                                        <Grid item xs={3}>
                                            <Typography variant={"caption"}>End date</Typography>
                                            <FormDateField
                                                placeholder="From"
                                                value={mapNumberToDate(actualEndDate).format(DATE_FORMAT)}
                                                onChange={newDate => {
                                                    setActualEndDate(convertDateStringToNumber(newDate));
                                                }}
                                                disabled={false}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid container item xs={12}>
                                        <Grid item xs={6}>
                                            <Typography variant="h6" className={classes.errorText}>
                                                {error}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </div>
                        </FormControl>
                    )}
                </DialogContent>
                <DialogActions>
                    <Tooltip title={!isSendEnabled ? "Please fill all fields" : ""}>
                        <span>
                            <BlueButton onClick={handleConfirmClick} disabled={!isSendEnabled}>
                                {dialogTitleMap[dialogType]?.confirmButtonTitle}
                            </BlueButton>
                        </span>
                    </Tooltip>
                    <BlueButton onClick={onClose}>Cancel</BlueButton>
                </DialogActions>
            </Dialog>
        </>
    );
};

export const CreateContractDialog = withStyles(styles)(CreateContractDialogPure);
